<script setup lang="ts">
// Events inside a selected time window (a click on a timeline selects a
// bucket, so a collapsed encode shows all of its destinations' events at
// once). Click an item to unfold its full text in a monospace block with a
// copy button; unfolding animates instead of jumping. A problem state shows
// how long it lasted (from `all`, the events around the window), and one
// already going on when the window starts is listed with it, marked.
import { computed, ref } from 'vue'
import type { Config, Event } from '../api'
import { useNode } from '../store'
import { dateTime, dot, dotClass, dur, entityLabel, maskURLs, stateEnds } from '../util'

const props = defineProps<{
    events: Event[]
    from: number
    to: number
    title: string
    cfg?: Config
    withEncode?: boolean
    all?: Event[]
}>()
const emit = defineEmits<{ close: [] }>()
const node = useNode()
const open = ref<number | null>(props.events.length === 1 ? props.events[0]!.id : null)
const sorted = computed(() => [...props.events].sort((a, b) => Date.parse(b.ts) - Date.parse(a.ts)))
// thousands of rows at once made the dialog slow to open: the newest first, the rest on request
const PAGE = 300
const shown = ref(PAGE)
const visible = computed(() => sorted.value.slice(0, shown.value))
const bad = computed(() => props.events.filter((e) => e.severity !== 'info').length)
// "for 14m" or "ongoing, 14m so far" for a problem state; '' otherwise
// when each state ended, in one pass (per row it was a scan of every event,
// and a dialog of thousands of events froze the page)
const ends = computed(() => (props.all ? stateEnds(props.all) : new Map<number, number | null>()))
function lasted(e: Event): string {
    if (e.type !== 'state' || e.severity === 'info' || !props.all) return ''
    const end = ends.value.get(e.id) ?? null
    const secs = ((end ?? node.now) - Date.parse(e.ts)) / 1000
    return end === null ? `ongoing, ${dur(secs)} so far` : `for ${dur(secs)}`
}
const before = (e: Event) => Date.parse(e.ts) < props.from
// the same, short for the row's small duration column: "14m", "5h", "2d"
// ("…" still going, "‹" began before the window); the words in its title
function short(e: Event): string {
    if (e.type !== 'state' || e.severity === 'info' || !props.all) return ''
    const end = ends.value.get(e.id) ?? null
    const secs = ((end ?? node.now) - Date.parse(e.ts)) / 1000
    const d =
        secs < 60
            ? `${Math.round(secs)}s`
            : secs < 3600
              ? `${Math.floor(secs / 60)}m`
              : secs < 86400
                ? `${Math.floor(secs / 3600)}h`
                : `${Math.floor(secs / 86400)}d`
    return `${before(e) ? '‹' : ''}${d}${end === null ? '…' : ''}`
}
const label = (e: Event) => entityLabel(props.cfg ?? node.config, e.kind, e.entity, props.withEncode)
const text = (e: Event) =>
    `${dateTime(e.ts)}  ${label(e)}${e.state ? '  ' + e.state : ''}${lasted(e) ? '  (' + lasted(e) + ')' : ''}\n${e.message}${e.actor ? `\nby ${e.actor}` : ''}`
async function copy(e: Event) {
    try {
        await navigator.clipboard.writeText(text(e))
        node.notify('Copied')
    } catch {
        node.notify('Copy failed; select the text instead')
    }
}
</script>

<template>
    <div class="fixed inset-0 z-20 flex items-center justify-center bg-black/55" @click.self="emit('close')">
        <div
            class="max-h-[85vh] max-w-[min(720px,96vw)] min-w-[min(560px,92vw)] overflow-auto rounded-[10px] border border-line-strong bg-bg-1 px-4.5 py-4"
            role="dialog"
        >
            <div class="mb-1 flex items-center gap-2">
                <b class="min-w-0 flex-1">{{ title }}</b>
                <button class="btn-ghost" aria-label="Close" @click="emit('close')">✕</button>
            </div>
            <div class="mb-2.5 text-xs text-fg-2">
                {{ dateTime(from) }} → {{ dateTime(to) }} · {{ dur((to - from) / 1000) }} · {{ events.length }} event{{
                    events.length === 1 ? '' : 's'
                }}<span v-if="bad"
                    >,
                    <span class="text-bad"
                        >{{ bad }} warning{{ bad === 1 ? '' : 's' }} or error{{ bad === 1 ? '' : 's' }}</span
                    ></span
                >
            </div>
            <div v-if="!events.length" class="text-fg-3">Nothing happened in this window.</div>
            <div v-for="e in visible" :key="e.id" class="border-t border-line" :class="{ 'bg-bg-2': open === e.id }">
                <!-- on a phone the time and the message take rows of their own:
                     the fixed columns alone were wider than the dialog (WEB-43) -->
                <div
                    class="grid cursor-pointer grid-cols-[150px_8px_minmax(90px,220px)_34px_1fr_12px] items-center gap-2 px-1 py-1.25 text-xs hover:bg-bg-2 max-[640px]:grid-cols-[8px_minmax(0,1fr)_34px_12px] max-[640px]:gap-y-0.5"
                    role="button"
                    tabindex="0"
                    :aria-expanded="open === e.id"
                    @click="open = open === e.id ? null : e.id"
                    @keydown.enter.prevent="open = open === e.id ? null : e.id"
                    @keydown.space.prevent="open = open === e.id ? null : e.id"
                >
                    <span class="font-mono text-fg-2 tabular-nums max-[640px]:col-span-full">{{ dateTime(e.ts) }}</span>
                    <span :class="e.state ? dotClass(e.state) : dot('ok')" />
                    <span class="truncate text-fg-2" :title="label(e)">{{ label(e) }}</span>
                    <span
                        class="text-right font-mono text-[10px] text-fg-3 tabular-nums"
                        :title="lasted(e) + (before(e) ? ', began before this window' : '')"
                        >{{ short(e) }}</span
                    >
                    <span class="truncate max-[640px]:order-2 max-[640px]:col-span-full"
                        >{{ maskURLs(e.message)
                        }}<span v-if="e.actor" class="text-fg-3"> · by {{ e.actor }}</span></span
                    >
                    <span class="text-right text-fg-3 max-[640px]:order-1">{{ open === e.id ? '▾' : '▸' }}</span>
                </div>
                <!-- graceful unfold: a grid row animates from 0fr to 1fr, so the height is never hard-coded -->
                <div
                    class="grid transition-[grid-template-rows] duration-120"
                    :class="open === e.id ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'"
                >
                    <div class="min-h-0 overflow-hidden">
                        <div v-if="open === e.id" class="relative px-1 pt-1 pb-2.5">
                            <pre
                                class="rounded-md border border-line bg-bg py-2 pr-16 pl-2.5 font-mono text-xs break-words whitespace-pre-wrap select-all"
                                >{{ text(e) }}</pre>
                            <button class="absolute top-2 right-2.5 btn-ghost text-xs" @click.stop="copy(e)">
                                copy
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <button v-if="sorted.length > shown" class="mt-2 btn-ghost text-xs" @click="shown += 1000">
                {{ sorted.length - shown }} older event{{ sorted.length - shown === 1 ? '' : 's' }}: show more
            </button>
        </div>
    </div>
</template>
